import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
import ProfileHeader from "../shared/ProfileHeader"
import { AtAGlanceContent } from "./AtAGlanceContent"
import { AvailabilityContent } from "./Availability/AvailabilityContent"
import { LocationsContent } from "./LocationsContent"
import { LocationsContentV2 } from "./LocationsContentV2"
import { SpecialitiesContent } from "../shared/SpecialitiesContent"
import { QualificationsContent } from "./QualificationsContent"
import { ClientFocusContent } from "../shared/ClientFocusContent"
import { TreatmentApproachContent } from "../shared/TreatmentApproachContent"
import { InsuranceContent } from "../shared/InsuranceContent"
import ProfileSection from "../shared/ProfileSection"
import { filterExpiredCredentials } from "../utils/qualificationsUtils"
import { ProviderProfileData } from "../ProviderProfileSlideOverContent"
import { PublicLocation } from "../../../domain/Profile/schema"
import { PracticeStatusEnum } from "../../../domain/Practice/schema"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import {
  selectCurrentUserRoles,
  UserRoles,
} from "../../../domain/Account/userSlice"
import { ProviderProfileSubSectionIds } from "../constants"
import { xUUID } from "../../../routes"
import { isEmpty } from "lodash"
import { ProviderTypeDisplayId } from "../../../domain/enums/displayId.enum"
import { LicenseStatusEnum } from "../../shared/utils"
import { hasLiabilityIssues } from "../utils/liabilityStatusHelpers"
import { hasVirtualSessionType } from "../utils/hasVirtualSessionType"
import { type RootState } from "../../../reduxStore/store"
import useContentLayoutContext from "../../layout/useContentLayoutContext"
import { LocationTypeDisplayId } from "../../../domain/Practice/schema"

interface ProviderProfileProps {
  profileData: ProviderProfileData
  isFromSearch?: boolean
}

const ProviderProfile: React.FC<ProviderProfileProps> = ({
  profileData,
  isFromSearch = false,
}) => {
  const { cms: cmsContent, isReady: isCMSReady } = useCMSReady([
    "publicProfileSlide",
  ])
  const publicProfileText = cmsContent.publicProfileSlide

  const { provider, publicPractices } = profileData

  const {
    biography,
    firstName,
    graduateDegreeDisplayId,
    graduateSchoolName,
    graduateYear,
    hasPatientWaitList,
    hasLicense,
    isAcceptingNewPatients,
    isAvailableEveningHours,
    id,
    isPublicViewable,
    isUnderSupervision,
    lastName,
    photoFileId,
    practicingSince,
    providerAvailableDisplayIds,
    providerCredentials,
    providerDesignationDisplayIds,
    providerDisclosures,
    providerEmails,
    providerInsuranceDisplayIds = [],
    providerLicenses,
    providerPhones,
    providerSessionTypeDisplayIds,
    providerSpecialties,
    providerTagDisplayIds,
    providerTherapyTypeDisplayIds,
    providerStatusDisplayId,
    providerTypeDisplayId: typeId,
    liabilityInsurances,
    userId,
    isVirtualAttested,
  } = provider || {}

  const userRoles = useSelector(selectCurrentUserRoles)
  const currentUserId = useSelector((state: RootState) => state.user.id)
  const { scrollableContainerRef } = useContentLayoutContext()

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )

  const canViewTags = [
    UserRoles.ROLE_AFFILIATE_RELATIONS,
    UserRoles.ROLE_EAP_RESEARCH,
  ].some((role) => userRoles.includes(role))
  const providerProfileTagDisplayIds =
    canViewTags && providerTagDisplayIds ? providerTagDisplayIds : []
  const profileName: string = `${firstName} ${lastName}`
  const primaryEmailAddress = (providerEmails ?? [])?.find(
    (email: { emailTypeDisplayId?: string }) =>
      email.emailTypeDisplayId === "primary",
  )?.address

  const nonExpiredCredentials = filterExpiredCredentials(
    providerCredentials ?? [],
  )
  const activePublicPractices = (publicPractices || [])
    .filter((practice) => practice.status === PracticeStatusEnum.ACTIVE)
    .map((practice) => ({
      ...practice,
      locations: (practice.locations ?? []).filter(
        (location) =>
          location.id &&
          (!isNoVirtualLocationEnabled ||
            location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL) &&
          provider?.providerLocations?.find((loc) => loc.id === location.id),
      ),
    }))

  const hasVirtualSession = hasVirtualSessionType(providerSessionTypeDisplayIds)

  const virtualReferralLocation: PublicLocation = {
    id: "",
    line1: "",
    line2: "",
    city: "",
    stateOrTerritory: "",
    postalCode: "",
    fax: "",
    isPrimary: false,
    isAdaAccessible: false,
    officeRequirementsCertified: false,
    locationTypeDisplayId: LocationTypeDisplayId.VIRTUAL,
    phones: [],
    emails: [],
    locationPhotos: [],
  }

  const referralDetails = [
    ...activePublicPractices.flatMap((practice) =>
      practice.locations.map((location) => ({
        location: { ...location },
        practiceInfo: {
          practiceName: practice.name,
          practiceId: practice.id,
          status: practice.status,
        },
        providerInfo: {
          providerName: profileName,
          providerId: id!,
          isUnderSupervision: !!isUnderSupervision,
          status: provider?.providerStatusDisplayId,
          providerTypeDisplayId: typeId,
        },
        practice,
        hasProviderLicense: hasLicense ?? false,
        providerLiabilityInsurances: liabilityInsurances,
        providerDisclosures: providerDisclosures,
      })),
    ),
    ...(isNoVirtualLocationEnabled && !!isVirtualAttested && hasVirtualSession
      ? activePublicPractices.map((practice) => ({
          location: virtualReferralLocation,
          practiceInfo: {
            practiceName: practice.name,
            practiceId: practice.id,
            status: practice.status,
          },
          providerInfo: {
            providerName: profileName,
            providerId: id!,
            isUnderSupervision: !!isUnderSupervision,
            status: provider?.providerStatusDisplayId,
            providerTypeDisplayId: typeId,
          },
          practice,
          hasProviderLicense: hasLicense ?? false,
          providerLiabilityInsurances: liabilityInsurances,
          providerDisclosures: providerDisclosures,
        }))
      : []),
  ]

  /**
   * Calculates banner data for license and liability warnings in the provider profile header.
   * Returns data indicating whether to show banners, their text, and styling.
   */
  const bannerData = useMemo(() => {
    if (!isCMSReady) {
      return {
        showLicenseBanner: false,
        licenseText: "",
        showLiabilityBanner: false,
        liabilityText: "",
        liabilityClickable: false,
        showDisclosureBanner: false,
        disclosureText: "",
      }
    }
    const hasLicenseIssues = !!provider?.providerLicenses?.some(
      (license) => license.licenseStatusDisplayId !== LicenseStatusEnum.ACTIVE,
    )

    const hasProviderLiabilityIssues = !!hasLiabilityIssues(
      activePublicPractices,
      provider?.liabilityInsurances,
    )

    const hasActiveDisclosure =
      !!provider?.providerDisclosures?.some(
        (disclosure) =>
          disclosure.fileId != null &&
          disclosure.envelopeId != null &&
          disclosure.archivedDateTime == null,
      ) || provider?.providerTypeDisplayId === ProviderTypeDisplayId.MANAGED // Managed providers don't have or need signed disclosures

    return {
      showLicenseBanner: hasLicenseIssues,
      licenseText: hasLicenseIssues
        ? publicProfileText?.licenseNeedsAttentionBannerText || ""
        : "",
      showLiabilityBanner: hasProviderLiabilityIssues,
      liabilityText: hasProviderLiabilityIssues
        ? publicProfileText?.liabilityNeedsAttentionBannerText || ""
        : "",
      liabilityClickable: true,
      showDisclosureBanner: !hasActiveDisclosure,
      disclosureText: !hasActiveDisclosure
        ? publicProfileText?.disclosureSignatureRequiredBannerText || ""
        : "",
      disclosureClickable: currentUserId === provider?.userId,
    }
  }, [
    isCMSReady,
    provider,
    activePublicPractices,
    publicProfileText?.licenseNeedsAttentionBannerText,
    publicProfileText?.liabilityNeedsAttentionBannerText,
    publicProfileText?.disclosureSignatureRequiredBannerText,
    currentUserId,
  ])

  const profileSections = [
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_AT_A_GLANCE_SECTION,
      label: publicProfileText?.atAGlanceSubsectionTitle || "",
      component: AtAGlanceContent,
      props: {
        biography,
        hasPatientWaitList,
        isAcceptingNewPatients,
        practicingSince,
        providerLicenses,
        providerSessionTypeDisplayIds,
        providerSpecialties,
        isUnderSupervision,
      },
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_AVAILABILITY_SECTION,
      label: publicProfileText?.availabilitySubsectionTitle || "",
      component: AvailabilityContent,
      props: {
        providerAvailableDisplayIds,
        isAvailableEveningHours,
      },
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_LOCATIONS_SECTION,
      label: publicProfileText?.locationsSubsectionTitle || "",
      component: isNoVirtualLocationEnabled
        ? LocationsContentV2
        : LocationsContent,
      props: isNoVirtualLocationEnabled
        ? {
            activePublicPractices,
            providerName: profileName,
            providerId: id,
            isUnderSupervision,
            providerStatusDisplayId,
            providerLiabilityInsurances: liabilityInsurances,
            hasLicense: hasLicense ?? false,
            providerDisclosures: providerDisclosures,
            providerTypeDisplayId: typeId,
            isFromSearch,
            isVirtualAttested: !!isVirtualAttested && hasVirtualSession,
            providerPhones: providerPhones ?? [],
          }
        : {
            activePublicPractices,
            providerName: profileName,
            providerId: id,
            isUnderSupervision,
            providerStatusDisplayId,
            providerLiabilityInsurances: liabilityInsurances,
            hasLicense: hasLicense ?? false,
            providerDisclosures: providerDisclosures,
            providerTypeDisplayId: typeId,
            isFromSearch,
          },
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_SPECIALITIES_SECTION,
      label: publicProfileText?.specialtiesSubsectionTitle || "",
      component: SpecialitiesContent,
      props: { providers: [provider] },
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_QUALIFICATIONS_SECTION,
      label: publicProfileText?.qualificationsSubsectionTitle || "",
      component: QualificationsContent,
      props: {
        credentials: nonExpiredCredentials,
        licenses: providerLicenses ?? [],
        graduateDegreeDisplayId: graduateDegreeDisplayId,
        graduateSchoolName: graduateSchoolName,
        graduateYear: graduateYear,
        isUnderSupervision,
      },
      showSection:
        !isEmpty(nonExpiredCredentials) ||
        !isEmpty(providerLicenses ?? []) ||
        [graduateDegreeDisplayId, graduateSchoolName, graduateYear].some(
          (d) => !!d,
        ),
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_CLIENT_FOCUS_SECTION,
      label: publicProfileText?.clientFocusSubsectionTitle || "",
      component: ClientFocusContent,
      props: { providers: [provider] },
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_TREATMENT_APPROACH_SECTION,
      label: publicProfileText?.treatmentApproachSubsectionTitle || "",
      component: TreatmentApproachContent,
      props:
        providerTherapyTypeDisplayIds &&
        providerTherapyTypeDisplayIds.length > 0
          ? { therapyTypes: providerTherapyTypeDisplayIds }
          : {},
    },
    {
      id: ProviderProfileSubSectionIds.PROVIDER_PROFILE_INSURANCE_SECTION,
      label: "Insurance",
      component: InsuranceContent,
      props: {
        insuranceDisplayIds: providerInsuranceDisplayIds,
        notAcceptingInsurance: providerInsuranceDisplayIds.length < 1,
      },
    },
  ].filter((section) => ("showSection" in section ? section.showSection : true))

  return (
    <>
      <ProfileHeader
        isPublicViewable={isPublicViewable ?? false}
        phoneNumberList={(providerPhones as []) ?? []}
        photoFileId={photoFileId}
        primaryEmailAddress={primaryEmailAddress ?? ""}
        profileId={id ?? xUUID}
        profileName={profileName}
        profileSections={profileSections}
        profileTagDisplayIds={providerProfileTagDisplayIds}
        profileType="providerProfile"
        providerDesignationDisplayIds={providerDesignationDisplayIds}
        referralDetails={referralDetails}
        isManaged={typeId === ProviderTypeDisplayId.MANAGED}
        isRegistered={!!userId}
        bannerData={bannerData}
        providerStatusDisplayId={providerStatusDisplayId}
      />
      <div
        className="overflow-y-auto pb-32 scroll-smooth"
        ref={scrollableContainerRef}
      >
        <div className="flex flex-col flex-grow gap-5 mt-5">
          {profileSections.map((item) => (
            <ProfileSection
              key={item.id}
              id={item.id}
              label={item.label}
              component={item.component}
              sectionProps={item?.props}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export default ProviderProfile
